<script setup>
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import PurchaseProjectCard from './PurchaseProjectCard.vue';
import { usePurchaseProjectsState } from './usePurchaseProjectsState.js';

const props = defineProps({
  contactId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(['view-all', 'open-project', 'create']);

const { t } = useI18n();
const { purchaseProjectsForContact, loadPurchaseProjects } =
  usePurchaseProjectsState();

const projects = computed(() => purchaseProjectsForContact(props.contactId));

const previewProjects = computed(() => projects.value.slice(0, 3));
const hasMore = computed(() => projects.value.length > 3);

const projectCountLabel = computed(() => {
  const n = projects.value.length;
  return n === 1
    ? t('PURCHASE_PROJECTS.PROJECT_SINGULAR')
    : t('PURCHASE_PROJECTS.PROJECTS_PLURAL', { count: n });
});

const loadProjects = () => loadPurchaseProjects(props.contactId);

onMounted(loadProjects);
watch(() => props.contactId, loadProjects);

const addProject = () => {
  loadPurchaseProjects(props.contactId, { force: true });
};

defineExpose({ addProject });
</script>

<template>
  <div class="flex flex-col mt-3 bg-n-solid-1">
    <div class="flex items-center justify-between px-4 py-3">
      <div class="flex flex-col gap-0.5">
        <span class="text-sm font-semibold text-n-slate-12">
          {{ t('PURCHASE_PROJECTS.PANEL_TITLE') }}
        </span>
        <span class="text-xs text-n-slate-10">
          {{ projectCountLabel }}
        </span>
      </div>
      <div class="flex items-center gap-1.5">
        <button
          type="button"
          class="flex items-center gap-1.5 rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs font-medium text-n-slate-11 transition-colors hover:text-n-slate-12"
          @click="emit('create')"
        >
          <span class="i-lucide-plus size-3.5" />
          {{ t('PURCHASE_PROJECTS.ADD') }}
        </button>
        <button
          v-if="projects.length > 0"
          type="button"
          class="flex items-center gap-1.5 rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs font-medium text-n-slate-11 transition-colors hover:text-n-slate-12"
          @click="emit('view-all')"
        >
          <span class="i-lucide-list size-3.5" />
          {{ t('PURCHASE_PROJECTS.SEE_ALL') }}
        </button>
      </div>
    </div>

    <div v-if="projects.length > 0" class="flex flex-col">
      <template v-for="(project, index) in previewProjects" :key="project.id">
        <div v-if="index > 0" class="h-px mx-4 bg-n-weak" />
        <PurchaseProjectCard
          :project="project"
          compact
          @open="emit('open-project', $event)"
        />
      </template>
    </div>

    <p v-else class="px-4 py-6 text-sm text-center text-n-slate-11">
      {{ t('PURCHASE_PROJECTS.EMPTY_STATE') }}
    </p>

    <button
      v-if="hasMore"
      type="button"
      class="mx-4 my-3 flex w-fit items-center gap-1.5 rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs font-medium text-n-slate-11 transition-colors hover:text-n-slate-12"
      @click="emit('view-all')"
    >
      <span class="i-lucide-list size-3.5" />
      {{ t('PURCHASE_PROJECTS.SEE_ALL_COUNT', { count: projects.length }) }}
    </button>
  </div>
</template>
